/**
 * MyraInteractionController.js
 *
 * Pure state + event controller for the three Myra interaction modes.
 * DOES NOT touch the DOM — React components read state via getters
 * and respond to events via on() subscriptions.
 *
 * USAGE IN REACT:
 *   import MyraInteraction from './MyraInteractionController';
 *
 *   // In a component:
 *   useEffect(() => {
 *     const unsub = MyraInteraction.on('modeChange', ({ mode }) => {
 *       setCurrentMode(mode);  // trigger React re-render
 *     });
 *     return unsub;
 *   }, []);
 *
 *   // Read state directly:
 *   MyraInteraction.getMode()        // 'voice' | 'chat' | 'button' | null
 *   MyraInteraction.isChatOpen()     // boolean
 *   MyraInteraction.isBubbleVisible() // boolean
 *
 * ALSO AVAILABLE GLOBALLY:
 *   window.MyraInteraction (for VH-Myra-FrontEnd integration)
 *
 * MODES:
 *   null     — user has never chosen (default on session start)
 *   'voice'  — Speak to Myra via microphone
 *   'chat'   — type messages to Myra
 *   'button' — on-screen buttons only; Myra speaks instructions via TTS
 */
import MyraChatService from "../voice-agent/MyraChatService";

class MyraInteractionController {
    constructor() {
        // ── Core State ──
        this._mode = null;              // null | 'voice' | 'chat' | 'button'
        this._micPermissionShown = false;
        this._micModalOpen = false;
        this._bubbleDismissed = false;
        this._bubbleGoneForever = false;
        this._bubbleVisible = true;     // starts visible
        this._fabExpanded = false;
        this._chatOpen = false;
        this._voiceOpen = false;
        this._chatMessages = [];        // Array of { sender: 'user'|'myra', text: string, ts: number }
        this._listeners = [];           // on() subscribers
        this._currentScreen = null;     // last screen name passed to onScreenEnter
        this._chatLoading = false;
        this._chatHistory = [];
        this._chatTurnNumber = 0;
        this._chatSelectedBefore = false;
        this._voiceSelectedBefore = false;
        this._buttonSelectedBefore = false;
		
		window.selectMyraMode = (modd) => {
			if ( modd == 'chat' ) {
				this.pickChat();
			} else if ( modd == 'voice' ) {
				this.pickVoice();
				
				if ( window.mayraCode &&  window.mayraCode.setupGlobalVirtualListener ) { window.mayraCode.setupGlobalVirtualListener(); }
			}
		}
    }

    // ════════════════════════════════════════════════════════════
    // STATE GETTERS — React reads these to decide what to render
    // ════════════════════════════════════════════════════════════

    /** @returns {string|null} 'voice' | 'chat' | 'button' | null */
    getMode() { return this._mode; }

    /** @returns {boolean} true if user has ever selected a mode */
    hasSelectedMode() { return this._mode !== null; }

    /** @returns {boolean} */
    isChatOpen() { return this._chatOpen; }

    /** @returns {boolean} */
    isVoiceOpen() { return this._voiceOpen; }

    /** @returns {boolean} */
    isFabExpanded() { return this._fabExpanded; }

    /** @returns {boolean} */
    isBubbleVisible() { return this._bubbleVisible; }

    /** @returns {boolean} */
    isMicModalOpen() { return this._micModalOpen; }

    /** @returns {boolean} */
    wasMicPermissionShown() { return this._micPermissionShown; }

    /**
     * Returns the full chat message history.
     * React should render this array directly.
     * @returns {Array<{sender: string, text: string, ts: number}>}
     */
    getChatMessages() { return this._chatMessages; }

    isChatLoading() { return this._chatLoading; }
    getChatHistory() { return this._chatHistory; }
    wasChatSelectedBefore() { return this._chatSelectedBefore; }
    setChatSelectedBefore() {
        this._chatSelectedBefore = true;
    }
    wasVoiceSelectedBefore() { return this._voiceSelectedBefore; }
    setVoiceSelectedBefore() {
        this._voiceSelectedBefore = true;
    }
    wasbuttonSelectedBefore() { return this._buttonSelectedBefore; }
    setButtonSelectedBefore() {
        this._buttonSelectedBefore = true;
    }
    /**
     * Returns what the Intro AR screen should display.
     * React calls this when rendering the Intro AR screen.
     *
     * @returns {{ showModeCard: boolean, showConfirmation: boolean, confirmationText: string }}
     */
    getIntroARState() {
        if (this._mode) {
            const labels = {
                voice: 'Speak to Myra (Voice)',
                chat: 'Chat with Myra',
                button: 'Tap Responses (Buttons)',
            };
            return {
                showModeCard: false,
                showConfirmation: true,
                confirmationText: '✓ Myra will communicate via: ' + (labels[this._mode] || this._mode),
            };
        }
        return {
            showModeCard: true,
            showConfirmation: false,
            confirmationText: '',
        };
    }

    // ════════════════════════════════════════════════════════════
    // PUBLIC API — Called from SelfAssessment.jsx
    // ════════════════════════════════════════════════════════════

    /**
     * Called when navigating to a new GSA screen.
     * Closes any open panels and updates bubble visibility.
     *
     * @param {string} screenName — e.g. 'problem_area', 'intro_ar', 'test_intro'
     */
    onScreenEnter(screenName) {
        this._currentScreen = screenName;
        this.closeFab();
        this.closeChat();
        this.closeVoice();
        this._updateBubbleForScreen(screenName);
        this._emit('screenEnter', { screen: screenName, mode: this._mode });
    }

    /**
     * Called when user presses Continue on the Intro AR screen.
     * If no mode was previously selected, reads the selection from the mode card.
     *
     * @param {string|null} cardSelection — 'voice' | 'chat' | 'button' | null
     */
    confirmIntroARMode(cardSelection) {
        if (!this._mode && cardSelection) {
            this._setMode(cardSelection);
        }
        // If voice was selected, show mic permission (first time only)
        if (this._mode === 'voice' && !this._micPermissionShown) {
            this.showMicPermission();
        }
    }

    // ════════════════════════════════════════════════════════════
    // PUBLIC API — Myra Fab Button
    // ════════════════════════════════════════════════════════════

    /**
     * Called when user taps the Myra fab button.
     * Behavior depends on current mode:
     *   - chat:  opens chat panel directly
     *   - voice: opens voice panel directly
     *   - button or null: toggles the expand menu (chat/Speak options)
     */
    onFabTap() {
        if (this._mode === 'chat') {
            this.openChat();
            return;
        }
        if (this._mode === 'voice') {
            this.openVoice();
            return;
        }
        this.toggleFab();
    }

    /**
     * Called when user selects "Chat with Myra" from the fab expand options.
     */
    pickChat() {
        this._setMode('chat');
        this.closeFab();
        this.openChat();
    }

    /**
     * Called when user selects "Speak to Myra" from the fab expand options.
     */
    pickVoice() {
        this._setMode('voice');
        this.closeFab();
        if (!this._micPermissionShown) {
            this.showMicPermission();
        } else {
            this.openVoice();
        }
    }

    // ════════════════════════════════════════════════════════════
    // PUBLIC API — Mode Switching (called from inside panels)
    // ════════════════════════════════════════════════════════════

    /**
     * Switch to a different interaction mode.
     * Closes current panels and opens the new one after a brief delay
     * (allows CSS transitions to complete).
     *
     * @param {string} newMode — 'voice' | 'chat' | 'button'
     */
    switchMode(newMode) {
        this.closeChat();
        this.closeVoice();
        this._setMode(newMode);

        if (newMode === 'chat') {
            setTimeout(() => this.openChat(), 200);
        } else if (newMode === 'voice') {
            if (!this._micPermissionShown) {
                setTimeout(() => this.showMicPermission(), 200);
            } else {
                setTimeout(() => this.openVoice(), 200);
            }
        }
        // 'button' — panels stay closed, user is back to on-screen buttons
    }

    // ════════════════════════════════════════════════════════════
    // PUBLIC API — Panel Controls
    // ════════════════════════════════════════════════════════════

    openChat() {
        this.closeFab();
        this._chatOpen = true;
        this._emit('chatOpen');
    }

    closeChat() {
        if (!this._chatOpen) return;
        this._chatOpen = false;
        this._emit('chatClose');
    }

    openVoice() {
        this.closeFab();
        this._voiceOpen = true;
        this._emit('voiceOpen');
    }

    closeVoice() {
        if (!this._voiceOpen) return;
        this._voiceOpen = false;
        this._emit('voiceClose');
    }

    toggleFab() {
        this._fabExpanded = !this._fabExpanded;
        this._emit('fabToggle', { expanded: this._fabExpanded });
    }

    closeFab() {
        if (!this._fabExpanded) return;
        this._fabExpanded = false;
        this._emit('fabToggle', { expanded: false });
    }

    // ════════════════════════════════════════════════════════════
    // PUBLIC API — Bubble
    // ════════════════════════════════════════════════════════════

    /**
     * Called when user taps the X on the bubble.
     * Hides bubble. It will reappear one final time on Intro AR,
     * then disappear forever.
     */
    dismissBubble() {
        this._bubbleDismissed = true;
        this._bubbleVisible = false;
        this._emit('bubbleDismiss');
    }

    // ════════════════════════════════════════════════════════════
    // PUBLIC API — Mic Permission
    // ════════════════════════════════════════════════════════════

    /**
     * Show the microphone permission modal.
     * Only shown once per session — subsequent voice activations skip it.
     */
    showMicPermission() {
        this._micPermissionShown = true;
        this._micModalOpen = true;
        this._emit('micPermissionShow');
    }

    /**
     * Close the mic permission modal.
     * Called from the modal's OK button.
     */
    closeMicPermission() {
        this._micModalOpen = false;
        this._emit('micPermissionClose');
    }

    // ════════════════════════════════════════════════════════════
    // PUBLIC API — Called from VH-Myra-FrontEnd / VoiceService
    // ════════════════════════════════════════════════════════════

    /**
     * Called by VoiceService when the browser denies mic permission.
     * Silently falls back to button mode. Does NOT show the mic modal again.
     */
    onMicPermissionDenied() {
        this._setMode('button');
        // this.closeVoice();
        this._micModalOpen = false;
        this._emit('micDenied');
    }

    /**
     * Called by VoiceService when WebSocket connection state changes.
     * React can listen for 'connectionChange' to show reconnecting states.
     *
     * @param {boolean} connected
     */
    onConnectionStateChange(connected) {
        this._emit('connectionChange', { connected, mode: this._mode });
    }

    /**
     * Called by VoiceService when Myra starts or stops speaking (TTS).
     * React can listen for 'agentSpeech' to update the voice panel
     * (e.g. "Myra is listening…" vs "Myra is speaking…").
     *
     * @param {boolean} speaking
     */
    onAgentSpeechChange(speaking) {
        // this._emit('agentSpeech', { speaking, mode: this._mode });
        // not adding the speech to chat
    }

    /**
     * Called by VoiceService when it receives a user voice command.
     * If in chat mode, the speech text is added to chat history
     * so it appears in the chat panel.
     *
     * @param {string} command — the recognized command name
     * @param {string} speech — the raw speech text
     * @param {string} intent — the intent classification
     */
    onVoiceCommand(command, speech, intent) {
        if (this._mode === 'chat') {
            // this._addChatMessage('user', speech);
            // not adding the command to chat
        }
        // this._emit('voiceCommand', { command, speech, intent, mode: this._mode });
    }

    /**
     * Called by VoiceService / Myra to display a response from Myra.
     * If in chat mode, the text is added to chat history.
     *
     * @param {string} text — Myra's message text
     */
    onMyraMessage(text) {
        if (this._mode === 'chat') {
            this._addChatMessage('myra', text);
        }
        this._emit('myraMessage', { text, mode: this._mode });
    }

    // ════════════════════════════════════════════════════════════
    // PUBLIC API — Chat Input
    // ════════════════════════════════════════════════════════════

    /**
     * Called when user sends a chat message (presses Enter or taps send).
     * Adds to chat history and emits 'chatMessageSent'.
     *
     * INTEGRATION NOTE: Chat is not yet implemented in the GSA backend.
     * The 'chatMessageSent' event currently has no handler.
     * When chat is implemented, either:
     *   (a) Wire this to VoiceService.sendTextForNLU() (WebSocket path), or
     *   (b) Wire this to MyraChatService.sendMessage() (REST path)
     * The handler should call onMyraMessage() with Myra's response text.
     *
     * @param {string} text — the user's typed message
     */
    // async sendChatMessage(text) {
    //     if (!text || !text.trim()) return;
    //     this._addChatMessage('user', text.trim());
    //     this._emit('chatMessageSent', { text: text.trim() });
    // }
    getRpeResponseText(normalized) {
        let agentReply = "";
        if (normalized >= 0 && normalized <= 2) {
            agentReply = "Easy—great to hear! Let’s move on to the next step.";
        } else if (normalized >= 3 && normalized <= 4) {
            agentReply = "Kinda Easy—great to hear! Let’s move on to the next step.";
        } else if (normalized >= 5 && normalized <= 6) {
            agentReply = "Just right—great! Let’s move on to the next step.";
        } else if (normalized >= 7 && normalized <= 8) {
            agentReply = "Kinda Hard—thanks for letting me know. Let’s move on to the next step.";
        } else if (normalized >= 9 && normalized <= 10) {
            agentReply = "Hard—thanks for letting me know. Let’s move on to the next step.";
        }
        return agentReply;
    }
    getCurrentScreen() { return this._currentScreen; }
    resolveMoveActionResponse(action, value) {
        switch (action) {
            case "SELECT_RPE":
                return this.getRpeResponseText(parseInt(value));

            case "SKIP_VIDEO":
            case "MOVE_ON":
                return "Got it!";

            case "REPLAY_VIDEO":
                return "Sure! I'll replay the video for you.";

            case "PAUSE_VIDEO":
                return "Sure! I'll pause the video for you.";

            case "RESUME_VIDEO":
                return "Sure! I'll resume the video for you.";

            case "EXPAND_VIDEO":
                return "Sure! I'll expand the video for you.";

            case "INVALID_COMMAND":
                return "I didn't catch that. You can say: Skip, Replay, Pause, or Expand.";

            case "VALID_COMMAND":
                return "Here’s what I can help with: Skip, Replay, Pause, Expand.";

            case "SELECT_SYMPTOM":
                if (value === "WORSENED" || value === "WORSE") {
                    return "Thanks for sharing—that’s expected from this test. Let’s move to the next step.";
                }
                return "Thanks for sharing that! Let’s move to the next step.";

            case "GIVE_NAME":
                return `Nice to meet you, ${value || "there"}! Let’s continue.`;

            case "SKIP_NAME":
                return "No problem, let’s continue.";

            default:
                return null;
        }
    };



    async sendChatMessage(text, screenPayload = {}) {
        if (!text || !text.trim()) return;
        if (this._chatLoading) return;

        this._addChatMessage('user', text.trim());
        this._chatLoading = true;
        this._emit('chatLoadingChange', { loading: true });

        const sessionId = window.trSessionId || localStorage.getItem("trSessionId");

        let screenName = this._currentScreen || "";
        if (screenName === "intro_ar") {
            screenName = "test1_demo"
        }


        const result = await MyraChatService.sendMessage(
            text.trim(),
            sessionId,
            this._chatHistory,
            this._chatTurnNumber,
            screenPayload
        );

        if (result) {
            this._chatTurnNumber = result.turnNumber || this._chatTurnNumber;
            this._chatHistory = result.conversationHistory || this._chatHistory;
        }

        let responseText = result?.response;
        if (/```json[\s\S]*?```/.test(responseText)) {
            responseText = "Sorry, I didn't quite catch that. Could you say that again?";
        }
        if (result?.moveAction) {
            const resolved = this.resolveMoveActionResponse(result.action, result.value);
            if (resolved) {
                responseText = resolved
            }
        }

        this._addChatMessage('myra', responseText);
        this._chatLoading = false;
        this._emit('chatLoadingChange', { loading: false });
    }

    // ════════════════════════════════════════════════════════════
    // PUBLIC API — Event Subscription
    // ════════════════════════════════════════════════════════════

    /**
     * Subscribe to controller events.
     * Returns an unsubscribe function.
     *
     * Events emitted:
     *   'modeChange'        — { mode, previousMode }
     *   'screenEnter'       — { screen, mode }
     *   'chatOpen'          — {}
     *   'chatClose'         — {}
     *   'voiceOpen'         — {}
     *   'voiceClose'        — {}
     *   'fabToggle'         — { expanded }
     *   'bubbleDismiss'     — {}
     *   'bubbleChange'      — { visible }
     *   'micPermissionShow' — {}
     *   'micPermissionClose'— {}
     *   'micDenied'         — {}
     *   'connectionChange'  — { connected, mode }
     *   'agentSpeech'       — { speaking, mode }
     *   'voiceCommand'      — { command, speech, intent, mode }
     *   'myraMessage'       — { text, mode }
     *   'chatMessageSent'   — { text }
     *   'chatHistoryUpdate' — { messages }
     *
     * @param {string} event
     * @param {function} callback
     * @returns {function} unsubscribe
     */
    on(event, callback) {
        const listener = { event, callback };
        this._listeners.push(listener);
        return () => {
            this._listeners = this._listeners.filter(l => l !== listener);
        };
    }

    // ════════════════════════════════════════════════════════════
    // PUBLIC API — State Reset
    // ════════════════════════════════════════════════════════════

    /**
     * Reset all interaction state. Called on assessment restart / exit.
     */
    reset() {
        this._mode = null;
        this._micPermissionShown = false;
        this._micModalOpen = false;
        this._bubbleDismissed = false;
        this._bubbleGoneForever = false;
        this._bubbleVisible = true;
        this._fabExpanded = false;
        this._chatOpen = false;
        this._voiceOpen = false;
        this._chatMessages = [];
        // this._currentScreen = null;
        this._emit('reset');
    }

    // ════════════════════════════════════════════════════════════
    // INTERNAL
    // ════════════════════════════════════════════════════════════

    /** @private */
    _setMode(mode) {
        const prev = this._mode;
        this._mode = mode;
        if (prev !== mode) {
            this._emit('modeChange', { mode, previousMode: prev });
        }
    }

    /** @private */
    _updateBubbleForScreen(screenName) {
        if (this._bubbleGoneForever) {
            this._bubbleVisible = false;
            this._emit('bubbleChange', { visible: false });
            return;
        }

        if (this._bubbleDismissed) {
            if (screenName === 'intro_ar') {
                // Show one final time on Intro AR
                this._bubbleVisible = true;
                this._bubbleGoneForever = true; // next dismiss or next screen hides forever
            } else {
                this._bubbleVisible = false;
            }
            this._emit('bubbleChange', { visible: this._bubbleVisible });
            return;
        }

        // Never dismissed — always visible
        this._bubbleVisible = true;
        this._emit('bubbleChange', { visible: true });
    }

    /** @private */
    _addChatMessage(sender, text) {
        this._chatMessages.push({ sender, text, ts: Date.now() });
        this._emit('chatHistoryUpdate', { messages: this._chatMessages });
    }

    /** @private */
    _emit(event, data = {}) {
        this._listeners
            .filter(l => l.event === event)
            .forEach(l => {
                try {
                    l.callback(data);
                } catch (err) {
                    console.error(`[MyraInteraction] Error in ${event} listener:`, err);
                }
            });
    }
}

// ════════════════════════════════════════════════════════════
// SINGLETON — available globally for VoiceService integration
// ════════════════════════════════════════════════════════════
if (!window.MyraInteraction) {
    window.MyraInteraction = new MyraInteractionController();
}

export default window.MyraInteraction;